import type { ParsedUrlQuery } from "querystring";

import { CURRENCY_URL_PARAM } from "@/features/display-currency/currency-url";
import { SEARCH_ROUTE_PARAM } from "@/features/search/api/route";
import type {
  Facet,
  Taxonomy,
  TaxonomyNode,
} from "@/features/search/api/types";
import { isTermsFacet } from "@/features/search/api/types";

/**
 * Helpers for the category taxonomy widget.
 *
 * The service returns the tree without counts on purpose: the `category` facet already
 * reports them per English slug, scoped to whatever else is filtered, which is exactly the
 * "how many if I went there" number a navigation row wants. Joining the two here keeps one
 * source for counts.
 */

/** Category facet counts, keyed by the English slug that taxonomy nodes carry. */
export const getCategoryCounts = (
  facets: Facet[] | undefined
): ReadonlyMap<string, number> => {
  const facet = facets?.find(candidate => candidate.key === "category");

  if (!facet || !isTermsFacet(facet)) {
    return new Map();
  }

  return new Map(facet.values.map(value => [value.value, value.count]));
};

/**
 * The query string to carry when moving to another category's landing page.
 *
 * Refinements survive — losing `?brand=vitra` because you switched from 3-seaters to
 * 2-seaters throws away the user's work. The page's identity does not: the catch-all segment
 * is the destination's own path, and `page` is meaningless against a new result set. A carried
 * filter can leave you on zero results, which is why the active chips stay visible — the cause
 * is legible and one click away from being removed. The display currency isn't a refinement:
 * internal links stay clean (WHO-6199), and the middleware puts it back on a full load.
 */
export const getCarriedQuery = (query: ParsedUrlQuery): ParsedUrlQuery => {
  const {
    [SEARCH_ROUTE_PARAM]: _segments,
    page: _page,
    [CURRENCY_URL_PARAM]: _currency,
    ...carried
  } = query;
  return carried;
};

/** One row of the widget: a node, where it sits, and where clicking it goes. */
export type TaxonomyRow = {
  slug: string;
  title: string;
  count?: number;
  /** Nesting level, for indentation. */
  depth: number;
  /** The anchor the results are currently narrowed to. */
  isCurrent: boolean;
  /**
   * Where the row navigates. Null means "leave the taxonomy" — clearing the category on a
   * page that only filters by it, and nothing at all on a root landing page, which has no
   * broader page to widen to.
   */
  destination: TaxonomyNode | null;
};

/** An empty category is a dead end — never offer it as somewhere to go. */
const hasItems = (row: TaxonomyRow) => (row.count ?? 0) > 0;

/** Busiest first, like the flat facets: the most stock is the most likely destination. */
const byCount = (left: TaxonomyRow, right: TaxonomyRow) =>
  (right.count ?? 0) - (left.count ?? 0);

/**
 * Flatten the tree into the rows to render: the path down to the anchor, then one step
 * further — its children, or its siblings when there are none to offer.
 *
 * Only that last group is filtered and sorted. The path is a hierarchy, so its order is the
 * tree's, not the counts', and the anchor stays even at zero: dropping it would leave a
 * headless widget on a page that happens to be empty.
 *
 * Siblings are a fallback rather than an addition because a leaf is where this matters most:
 * `/furniture/sofas/3-5-seaters` has no children, and a children-only widget would render
 * nothing exactly where the shopper needs to move sideways. The fallback also covers a node
 * whose children all turn out to be empty — those drop out, and siblings stand in.
 *
 * With no anchor (`/new`, `/sale`, a bare query search) the path is empty and `children` are
 * the root categories, which makes the same widget an entry point into the tree.
 *
 * The anchor row navigates to its parent: "turn this category off" and "go up one" are the
 * same move, and it is the row people reach for.
 */
export const getTaxonomyRows = (
  taxonomy: Taxonomy,
  counts: ReadonlyMap<string, number>
): { path: TaxonomyRow[]; nextLevel: TaxonomyRow[] } => {
  const toRow = (
    node: TaxonomyNode,
    depth: number,
    destination: TaxonomyNode | null = node
  ): TaxonomyRow => ({
    slug: node.slug,
    title: node.title,
    count: counts.get(node.slug),
    depth,
    isCurrent: false,
    destination,
  });

  const anchorDepth = Math.max(taxonomy.path.length - 1, 0);

  const path = taxonomy.path.map((node, index) => {
    const isAnchor = index === taxonomy.path.length - 1;

    return {
      ...toRow(node, index, isAnchor ? taxonomy.path[index - 1] ?? null : node),
      isCurrent: isAnchor,
    };
  });

  const children = taxonomy.children
    .map(node => toRow(node, taxonomy.path.length))
    .filter(hasItems)
    .sort(byCount);
  const siblings = taxonomy.siblings
    .map(node => toRow(node, anchorDepth))
    .filter(hasItems)
    .sort(byCount);

  return { path, nextLevel: children.length ? children : siblings };
};
